import { Either, Validation } from 'monet';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Префикс имени шаблона.
 * @type {string}
 */
const PREFIX_NAME = 'ОБРАЗЕЦ';

/**
 * @description Запрос на проверку шаблона клиники.
 * @param {number} id
 * @param {File} file
 * @constructor
 */
export function ClinicTemplateRequestPreview(id, file) {
  this.id = id;
  this.file = file;

  /**
   * @description К запросу.
   * @returns {{template: *, name}}
   */
  this.toQuery = () => {
    const data = new FormData();
    data.append('template_id', this.id);
    if (this.file && this.file.size) {
      data.append('template', this.file);
    }
    data.append('_method', 'POST');
    return data;
  };
}

/**
 * @description Запрос на редактирование шаблона клиники.
 * @param {number} id
 * @param {string} name
 * @param {File} file
 * @constructor
 */
export function ClinicTemplateRequestEdit(id, name, file) {
  this.id = id;
  this.name = name;
  this.file = file;

  /**
   * @description К запросу.
   * @returns {{template: *, name}}
   */
  this.toQuery = () => {
    const data = new FormData();
    data.append('name', this.name);
    if (this.file && this.file.size) {
      data.append('template', this.file);
    }
    data.append('_method', 'PUT');
    return data;
  };

  /**
   * @description Получить Id шаблона.
   * @returns {number}
   */
  this.getTemplateId = () => this.id;
}

/**
 * @description Запрос на создание шаблона клиники.
 * @param {string} name
 * @param {File} file
 * @constructor
 */
export function ClinicTemplateRequestCreate(name, file) {
  this.name = name;
  this.file = file;

  /**
   * @description К запросу.
   * @returns {{template: *, name}}
   */
  this.toQuery = () => {
    const data = new FormData();
    data.append('name', this.name);
    if (this.file && this.file.size) {
      data.append('template', this.file);
    }
    data.append('_method', 'POST');
    return data;
  };
}

/**
 * @description Шаблон
 @param {string[]} allowedActions Допустимые действия.
 @param {number} id               Id
 @param {boolean} isSample        Образец ли
 @param {string} name             Имя
 @param {string} removingId       Идентификатор для удаления.
 @param {string} type             Тип
 @param {string} typeName         Имя типа
 @param {string} fileName         Имя типа
 * @constructor
 */
function Template(allowedActions, id, isSample, name, removingId, type, typeName, fileName) {
  this.id = id;
  this.isCanBeRemoved = allowedActions.includes('remove');
  this.isSample = isSample;
  this.name = name;
  this.removingId = removingId;
  this.type = type;
  this.typeName = typeName;
  this.fileName = fileName;

  /**
   * @description Получить имя с префиксом.
   * @returns {string}
   */
  this.getNameWithPrefix = () => (this.isSample ? `${PREFIX_NAME} ${this.name}` : this.name);
}

/**
 * @description Шлюз шаблонов документов.
 * @param {Axios} axios
 */
export default function DocumentTemplateGateway(axios) {
  /**
   * @description Добавить пациенту шаблон.
   * @param {string} patientId
   * @param {string} templateId
   * @param {string} templateType
   * @param {boolean} showOnAllBranches
   * @returns {Promise<Either>}
   */
  this.addPatientTemplate = (
    patientId,
    templateId,
    templateType,
    showOnAllBranches,
  ) => axios
    .post(
      `/api/patient/${patientId}/template/${templateId}`,
      { template_type: templateType, show_on_all_branches: showOnAllBranches },
    ).then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Добавить пациенту шаблон по образцу.
   * @param {string} patientId
   * @param {string} sampleId
   * @param {string} templateType
   * @param {boolean} showOnAllBranches
   * @returns {Promise<Either>}
   */
  this.addPatientTemplateBySample = (
    patientId,
    sampleId,
    templateType,
    showOnAllBranches,
  ) => axios
    .post(
      `/api/patient/${patientId}/sample/${sampleId}`,
      { template_type: templateType, show_on_all_branches: showOnAllBranches },
    ).then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicTemplateRequestEdit} request
   * @returns {Promise<Either>}
   */
  this.editClinicDocumentTemplate = (request) => axios
    .post(`/api/templates/document/${request.getTemplateId()}`, request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicTemplateRequestPreview} request
   * @returns {Promise<Either>}
   */
  this.previewClinicDocumentTemplate = (request) => axios
    .post(
      '/api/templates/document/preview',
      request.toQuery(),
      {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          const { causes, title } = data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @returns {Promise<Either>}
   */
  this.downloadTemplateVariables = () => axios
    .get(
      '/api/templates/variables/download',
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Предпросмотр шаблона документа клиники.
   * @param {ClinicTemplateRequestPreview} request
   * @returns {Promise<Either>}
   */
  this.previewClinicContractTemplate = (request) => axios
    .post(
      '/api/templates/contract/preview',
      request.toQuery(),
      {
        headers: { accept: 'application/pdf' },
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const json = await (error.response.data || '').text();
          const data = JSON.parse(json);
          const { causes, title } = data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Загрузить шаблон документа клиники.
   * @param {String} templateId
   * @returns {Promise<Either>}
   */
  this.downloadClinicDocumentTemplate = (templateId) => axios
    .get(
      `/api/templates/document/download/${templateId}`,
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Загрузить шаблон договора клиники.
   * @param {String} templateId
   * @returns {Promise<Either>}
   */
  this.downloadClinicContractTemplate = (templateId) => axios
    .get(
      `/api/templates/contract/download/${templateId}`,
      {
        responseType: 'blob',
      },
    )
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicTemplateRequestEdit} request
   * @returns {Promise<Either>}
   */
  this.editClinicContractTemplate = (request) => axios
    .post(`/api/templates/contract/${request.getTemplateId()}`, request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон документа клиники.
   * @param {ClinicTemplateRequestCreate} request
   * @returns {Promise<Either>}
   */
  this.storeClinicDocumentTemplate = (request) => axios
    .post('/api/templates/document', request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить шаблон договора клиники.
   * @param {ClinicTemplateRequestCreate} request
   * @returns {Promise<Either>}
   */
  this.storeClinicContractTemplate = (request) => axios
    .post('/api/templates/contract', request.toQuery())
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { causes, title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удалить документ у пациента.
   *
   * @param {string} patientId
   * @param {string} templateId
   * @param {string} type
   * @returns {*}
   */
  this.deletePatientTemplate = (patientId, templateId, type) => axios
    .delete(`/api/patient/${patientId}/template/${templateId}`, { data: { type } })
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить шаблон.
   * @param {string} templateId Идентификатор шаблона.
   * @returns {Promise<Either>}
   */
  this.deleteTemplate = (templateId) => axios
    .delete(`/api/document-templates/${templateId}`)
    .then(
      (response) => Either.of(response.data),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 404
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список шаблонов для просмотра.
   * @param {string} search
   * @returns {Promise<Either>}
   */
  this.viewList = (search = '') => axios
    .get('/api/templates', { params: { search } })
    .then(
      (response) => {
        const templates = response.data.data.map((item) => new Template(
          item.allowed_actions,
          item.id,
          item.is_sample,
          item.name,
          item.removing_id,
          item.type,
          item.type_name,
          item.file_name,
        ));
        return Either.of(templates);
      },
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить список шаблонов по пациенту для просмотра.
   * @param {string} patientId
   * @param {string} search
   * @param {string} sort
   * @returns {Promise<Either>}
   */
  this.viewPatientList = (patientId, search = '', sort = '') => axios
    .get('/api/patient-templates', { params: { patient_id: patientId, search, sort } })
    .then(
      (response) => Either.of({
        list: response.data.data.list,
        patientUnderFifteen: response.data.data.patient_under_fifteen,
      }),
      (error) => {
        if (
          error.isAxiosError && (
            error.response.status === 400
            || error.response.status === 403
          )
        ) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Получить сгенерированный документ.
   * @param {number} templateId Идентификатор.
   * @returns {Promise<Validation>}
   */
  this.generate = (templateId) => axios
    .get(`/api/templates/${templateId}`)
    .then(async (response) => Validation.Success(response.data));
}
